import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from '@glu/form-components';
import { CalendarNextIcon, CalendarIcon } from '@glu/icons-react';
import { AccessibilityText } from '@glu/utilities-react';
import { createUseStyles, withTheme } from '@glu/theming';
import locale from '@glu/locale';
import classnames from 'classnames';
import { isValidSelection } from '../utils/dateCompare.js';
import styles from './TextInput.styles';
import { currentRangeDelimiter, datesToString } from '../utils/formatString.js';
import useKeyboardControls from './useKeyboardControls.js';

const useStyles = createUseStyles(styles);

const TextInput = (props) => {
  const {
    dark,
    dateMax,
    dateMin,
    disabled,
    error,
    errors,
    format,
    formats,
    handleApply,
    handleClose,
    htmlId,
    inputRef,
    inputValue,
    labelText,
    name,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    open,
    optional,
    placeholder,
    rangeDelimiters,
    screenReaderLabel,
    selectedEndDate,
    selectedStartDate,
    setConfirmedEndDate,
    setConfirmedStartDate,
    setFirstFocusedElement,
    setHead,
    setInputValue,
    setOpen,
    setSelectedEndDate,
    setSelectedStartDate,
    singleDatePicker,
    stringToDates,
    theme,
  } = props;
  const classes = useStyles();
  const [inputHasChanged, setInputHasChanged] = useState(false);

  const convertStringToDates = (string) =>
    stringToDates(string, singleDatePicker, rangeDelimiters, formats, [
      selectedStartDate,
      selectedEndDate,
    ]);

  const handleInputChange = ({ target: { value } }) => {
    setInputHasChanged(true);
    if (!value) {
      setConfirmedStartDate(undefined);
      setConfirmedEndDate(undefined);
      setSelectedStartDate(undefined);
      setSelectedEndDate(undefined);
      setInputValue(value);
      onChange(name, []);
      return;
    }

    const [start, end] = convertStringToDates(value);

    setInputValue(value);

    // Set start first
    if (isValidSelection(start, [dateMin, dateMax])) {
      setHead(start);
      setSelectedStartDate(start);
      // Finish workflow early if only choosing one date
      if (singleDatePicker) {
        setSelectedEndDate(start);
      }
      // Write end date
      if (isValidSelection(end, [dateMin, dateMax]) && end?.valueOf() >= start.valueOf()) {
        setSelectedEndDate(end);
      }
    }
  };

  /* istanbul ignore next */
  const handleInputBlur = (e) => {
    const { target: { value } = {} } = e;
    const dates = !value ? [] : convertStringToDates(value);
    const rangeDelimiter = currentRangeDelimiter(value, singleDatePicker, rangeDelimiters);
    const [start, end] = dates;
    if (!open) {
      onBlur();
    }
    if (inputHasChanged) {
      setInputValue(datesToString(start, end, { format, rangeDelimiter, singleDatePicker }));
      onChange(name, dates, true);
      setInputHasChanged(false);
    } else {
      e.stopPropagation();
    }
  };

  const { handleInputKeyDown, handleWrapperKeyDown } = useKeyboardControls({
    convertStringToDates,
    handleApply,
    handleClose,
    setFirstFocusedElement,
    open,
    setOpen,
  });

  const handleOnKeyDown = (e) => {
    onKeyDown(e);
    handleInputKeyDown(e);
  };

  const handleOnClick = () => {
    if (!disabled) {
      setOpen((o) => !o);
    }
  };

  return (
    <div onClick={handleOnClick} onKeyDown={handleWrapperKeyDown} role="presentation">
      <Input
        autoComplete="off"
        className={classnames(classes.inputWrapper, { [classes.dark]: dark })}
        dark={dark}
        data-qa="text-input"
        disabled={disabled}
        disableInputAPI
        error={error || errors?.[0]}
        htmlId={htmlId}
        inputElementRef={inputRef}
        labelText={labelText}
        name={name}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onFocus={(event) => {
          onFocus(event);
        }}
        onKeyDown={handleOnKeyDown}
        optional={optional}
        placeholder={placeholder}
        screenReaderLabel={screenReaderLabel}
        type="text"
        value={inputValue}
      />
      <button
        aria-controls={`${htmlId}-popup`}
        aria-expanded={open}
        aria-haspopup="true"
        className={classnames(
          classes.toggle,
          { [classes.screenReaderLabel]: !labelText || screenReaderLabel },
          { [classes.disabledToggle]: disabled }
        )}
        data-qa="toggle-button"
        disabled={disabled}
        id={`${htmlId}-toggle`}
        tabIndex="-1"
        type="button"
      >
        <AccessibilityText>
          {!singleDatePicker ? locale.get('chooseDateRange') : locale.get('chooseDate')}
        </AccessibilityText>
        {theme.name === 'legacy' ? (
          <CalendarIcon className={classnames(classes.inputIcon, { [classes.dark]: dark })} />
        ) : (
          <CalendarNextIcon className={classnames(classes.inputIcon, { [classes.dark]: dark })} />
        )}
      </button>
    </div>
  );
};

TextInput.propTypes = {
  dark: PropTypes.bool,
  dateMax: PropTypes.string.isRequired,
  dateMin: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  /** format to convert dates to string */
  format: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  /** all possible formats that a string could be in.
   * used when converting a string into date object */
  formats: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleApply: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  htmlId: PropTypes.string.isRequired,
  inputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  inputValue: PropTypes.string,
  labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  open: PropTypes.bool.isRequired,
  optional: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
  rangeDelimiters: PropTypes.arrayOf(PropTypes.string).isRequired,
  screenReaderLabel: PropTypes.bool.isRequired,
  selectedEndDate: PropTypes.shape({}),
  selectedStartDate: PropTypes.shape({}),
  setConfirmedEndDate: PropTypes.func.isRequired,
  setConfirmedStartDate: PropTypes.func.isRequired,
  setFirstFocusedElement: PropTypes.func.isRequired,
  setHead: PropTypes.func.isRequired,
  setInputValue: PropTypes.func.isRequired,
  setOpen: PropTypes.func.isRequired,
  setSelectedEndDate: PropTypes.func.isRequired,
  setSelectedStartDate: PropTypes.func.isRequired,
  singleDatePicker: PropTypes.bool.isRequired,
  stringToDates: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    name: PropTypes.string,
  }),
};

TextInput.defaultProps = {
  dark: false,
  disabled: false,
  error: '',
  errors: [],
  inputValue: '',
  onChange: /* istanbul ignore next */ () => {},
  onFocus: /* istanbul ignore next */ () => {},
  onKeyDown: /* istanbul ignore next */ () => {},
  optional: false,
  selectedEndDate: undefined,
  selectedStartDate: undefined,
  theme: { name: 'ashTheme' },
};

export default withTheme(TextInput);
